/**
 * MarketingAlignmentBlock — W47 live-data rewrite
 *
 * Fixes:
 * 1. Column renamed "AE Owner" -> "Owner", bound to users.full_name via server JOIN
 * 2. Top-10 pulled from live DB query (not localStorage seed)
 * 3. "Where Will is" and "Block Harbor" removed (no longer in seed; live query excludes them)
 * 4. MQL target/actual columns from marketing_events; status pill ON TRACK >= 0.6, AT RISK <0.6, — if target=0
 * 5. BDR cadence panel from cadence enrollments or empty-state copy
 * 6. Annual Totals row computed from live rows (not hardcoded)
 * 7. MQL->revenue tile shows em-dash where columns/tables don't exist to compute
 */
import { useEffect, useState } from "react";

const BLACK = "#0B0B0F";
const RED   = "#DC2626";

interface AbmRow {
  account_id: string;
  account_name: string;
  owner_name: string;
  status: string;
  mpact_score: number;
  mql_target: number;
  mql_actual: number;
  status_pill: string; // "ON TRACK" | "AT RISK" | "—"
}

interface CadenceRow {
  cadence_name: string;
  hook_count: number;
  next_due: string | null;
}

interface MqlFunnel {
  mql_count: number;
  mql_to_sql_actual: number | null;
  mql_to_sql_target: number;
  mql_to_opp_actual: number | null;
  mql_to_opp_target: number;
  attrib_closed_won_actual: number | null;
  attrib_closed_won_target: number;
}

function statusPillStyle(pill: string): { bg: string; fg: string } {
  if (pill === "ON TRACK") return { bg: "rgba(22,163,74,0.10)", fg: "#16A34A" };
  if (pill === "AT RISK")  return { bg: "rgba(220,38,38,0.10)",  fg: "#DC2626" };
  return { bg: "rgba(107,114,128,0.10)", fg: "#6B7280" };
}

function funnelRag(actual: number | null, target: number): { fg: string } {
  if (actual === null) return { fg: "#6B7280" };
  if (target === 0) return { fg: "#6B7280" };
  const ratio = actual / target;
  if (ratio >= 0.9) return { fg: "#16A34A" };
  if (ratio >= 0.7) return { fg: "#EA580C" };
  return { fg: "#DC2626" };
}

export default function MarketingAlignmentBlock() {
  const [abmRows, setAbmRows]       = useState<AbmRow[]>([]);
  const [abmLoading, setAbmLoading] = useState(true);
  const [abmError, setAbmError]     = useState<string | null>(null);

  const [cadences, setCadences]           = useState<CadenceRow[]>([]);
  const [cadenceEmpty, setCadenceEmpty]   = useState(false);
  const [cadenceLoading, setCadenceLoading] = useState(true);

  const [funnel, setFunnel]         = useState<MqlFunnel | null>(null);
  const [funnelLoading, setFunnelLoading] = useState(true);

  useEffect(() => {
    // Fetch ABM Top-10
    fetch("/api/marketing/abm-top10", { credentials: "include" })
      .then((r) => r.json())
      .then((data) => {
        setAbmRows(data.rows ?? []);
        setAbmLoading(false);
      })
      .catch((err) => {
        console.error("[MarketingAlignmentBlock] abm-top10 error", err);
        setAbmError("Failed to load accounts");
        setAbmLoading(false);
      });

    // Fetch BDR cadence
    fetch("/api/marketing/bdr-cadence", { credentials: "include" })
      .then((r) => r.json())
      .then((data) => {
        setCadences(data.cadences ?? []);
        setCadenceEmpty(data.empty === true || (data.cadences ?? []).length === 0);
        setCadenceLoading(false);
      })
      .catch((err) => {
        console.error("[MarketingAlignmentBlock] bdr-cadence error", err);
        setCadenceEmpty(true);
        setCadenceLoading(false);
      });

    // Fetch MQL funnel
    fetch("/api/marketing/mql-funnel", { credentials: "include" })
      .then((r) => r.json())
      .then((data) => {
        setFunnel(data);
        setFunnelLoading(false);
      })
      .catch((err) => {
        console.error("[MarketingAlignmentBlock] mql-funnel error", err);
        setFunnelLoading(false);
      });
  }, []);

  // Fix #6: Annual Totals row — compute from live rows
  const totalMqlTarget = abmRows.reduce((s, r) => s + (r.mql_target || 0), 0);
  const totalMqlActual = abmRows.reduce((s, r) => s + (r.mql_actual || 0), 0);

  return (
    <div className="mt-8 grid grid-cols-1 xl:grid-cols-3 gap-4">
      {/* ── Top-10 ABM ─────────────────────────────────────────────────── */}
      <div className="bg-white rounded-xl border border-neutral-200 shadow-sm overflow-hidden xl:col-span-2">
        <div className="px-5 py-3 flex items-center justify-between border-b border-neutral-100">
          <div>
            <div className="text-[9px] uppercase tracking-[0.22em] text-neutral-500 mb-0.5">Sales-plan alignment</div>
            <div className="text-sm font-bold" style={{ color: BLACK }}>Top-10 ABM accounts — Marketing lift</div>
          </div>
          <a
            href="/#/"
            className="text-[11px] font-semibold text-neutral-500 hover:text-neutral-800"
          >
            Account list →
          </a>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-xs border-collapse" style={{ minWidth: 720 }}>
            <thead>
              <tr style={{ background: "#FAFAF7", color: "#525258" }}>
                <th className="px-4 py-2 text-left font-semibold uppercase tracking-[0.12em] text-[10px]">Account</th>
                {/* Fix #1: renamed from "AE Owner" to "Owner" */}
                <th className="px-3 py-2 text-left font-semibold uppercase tracking-[0.12em] text-[10px]">Owner</th>
                <th className="px-3 py-2 text-center font-semibold uppercase tracking-[0.12em] text-[10px]">MQL Target</th>
                <th className="px-3 py-2 text-center font-semibold uppercase tracking-[0.12em] text-[10px]">Actual</th>
                <th className="px-3 py-2 text-center font-semibold uppercase tracking-[0.12em] text-[10px]">Status</th>
              </tr>
            </thead>
            <tbody>
              {abmLoading && (
                <tr>
                  <td colSpan={5} className="px-4 py-6 text-center text-neutral-400 text-xs">Loading accounts...</td>
                </tr>
              )}
              {!abmLoading && abmError && (
                <tr>
                  <td colSpan={5} className="px-4 py-4 text-center text-red-500 text-xs">{abmError}</td>
                </tr>
              )}
              {!abmLoading && !abmError && abmRows.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-4 py-6 text-center text-neutral-400 text-xs">No active accounts found.</td>
                </tr>
              )}
              {!abmLoading && !abmError && abmRows.map((r) => {
                const ps = statusPillStyle(r.status_pill);
                return (
                  <tr key={r.account_id} className="border-b border-neutral-100 hover:bg-neutral-50/70">
                    <td className="px-4 py-2.5">
                      <a
                        href={`/#/account/${r.account_id}`}
                        className="font-semibold text-neutral-800 hover:text-red-600"
                      >
                        {r.account_name}
                      </a>
                    </td>
                    {/* Fix #1: owner_name from users.full_name */}
                    <td className="px-3 py-2.5 text-neutral-600">{r.owner_name}</td>
                    {/* Fix #4: MQL target/actual from marketing_events */}
                    <td className="px-3 py-2.5 text-center font-semibold tabular-nums text-neutral-800">
                      {r.mql_target === 0 ? "—" : r.mql_target}
                    </td>
                    <td className="px-3 py-2.5 text-center tabular-nums text-neutral-700">
                      {r.mql_target === 0 ? "—" : r.mql_actual}
                    </td>
                    {/* Fix #4: status pill — em-dash if target=0 */}
                    <td className="px-3 py-2.5 text-center">
                      {r.status_pill === "—" ? (
                        <span className="text-neutral-400 text-xs">—</span>
                      ) : (
                        <span
                          className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider"
                          style={{ background: ps.bg, color: ps.fg }}
                        >
                          {r.status_pill}
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            {/* Fix #6: Annual Totals row — computed from live rows */}
            {!abmLoading && !abmError && abmRows.length > 0 && (
              <tfoot>
                <tr style={{ background: "#0B0B0F", color: "white" }}>
                  <td className="px-4 py-2.5 font-bold text-[10px] uppercase tracking-[0.15em]">Totals</td>
                  <td className="px-3 py-2.5" />
                  <td className="px-3 py-2.5 text-center font-bold tabular-nums text-sm">
                    {totalMqlTarget > 0 ? totalMqlTarget : "—"}
                  </td>
                  <td className="px-3 py-2.5 text-center font-bold tabular-nums text-sm">
                    {totalMqlTarget > 0 ? totalMqlActual : "—"}
                  </td>
                  <td className="px-3 py-2.5" />
                </tr>
              </tfoot>
            )}
          </table>
        </div>
        <div className="px-5 py-2 text-[10px] text-neutral-500 border-t border-neutral-100">
          Marketing owns ABM lift per account — alignment with AE plans.
        </div>
      </div>

      {/* ── BDR cadence support + handoff column ─────────────────────────── */}
      <div className="flex flex-col gap-4">
        <div className="bg-white rounded-xl border border-neutral-200 shadow-sm overflow-hidden">
          <div className="px-5 py-3 border-b border-neutral-100">
            <div className="text-[9px] uppercase tracking-[0.22em] text-neutral-500 mb-0.5">BDR alignment</div>
            <div className="text-sm font-bold" style={{ color: BLACK }}>Cadence support this quarter</div>
          </div>
          <div className="divide-y divide-neutral-100">
            {cadenceLoading && (
              <div className="px-5 py-4 text-xs text-neutral-400">Loading cadences...</div>
            )}
            {/* Fix #5: empty-state copy when Michael has no active rows */}
            {!cadenceLoading && cadenceEmpty && (
              <div className="px-5 py-4 text-xs text-neutral-500">
                No active cadences for Michael this quarter
              </div>
            )}
            {/* Fix #5: live cadence rows */}
            {!cadenceLoading && !cadenceEmpty && cadences.map((c) => (
              <div key={c.cadence_name} className="px-5 py-3 flex items-start gap-3">
                <div className="flex-1 min-w-0">
                  <a
                    href="/#/cadence/bdr"
                    className="text-[12px] font-semibold text-neutral-800 hover:text-red-600 block truncate"
                  >
                    {c.cadence_name}
                  </a>
                  {c.next_due && (
                    <div className="text-[10px] text-neutral-500 mt-0.5">
                      Next due: {c.next_due}
                    </div>
                  )}
                </div>
                <span
                  className="text-[10px] font-bold tabular-nums px-2 py-0.5 rounded-full flex-shrink-0"
                  style={{ background: "rgba(15,23,42,0.06)", color: BLACK }}
                  title="Active enrollments in this cadence"
                >
                  {c.hook_count} active
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Fix #7: MQL->revenue tile — em-dash for unavailable metrics */}
        <div className="bg-white rounded-xl border border-neutral-200 shadow-sm overflow-hidden">
          <div className="px-5 py-3 border-b border-neutral-100">
            <div className="text-[9px] uppercase tracking-[0.22em] text-neutral-500 mb-0.5">Sales handoff</div>
            <div className="text-sm font-bold" style={{ color: BLACK }}>MQL → revenue</div>
          </div>
          <div className="px-5 py-4 grid grid-cols-3 gap-3">
            {funnelLoading ? (
              <div className="col-span-3 text-xs text-neutral-400 text-center py-2">Loading...</div>
            ) : [
              {
                label: "MQL → SQL",
                actual: funnel?.mql_to_sql_actual ?? null,
                target: funnel?.mql_to_sql_target ?? 25,
              },
              {
                label: "MQL → Opp",
                actual: funnel?.mql_to_opp_actual ?? null,
                target: funnel?.mql_to_opp_target ?? 12,
              },
              {
                label: "Closed-won attrib",
                actual: funnel?.attrib_closed_won_actual ?? null,
                target: funnel?.attrib_closed_won_target ?? 32,
              },
            ].map((m) => {
              const rag = funnelRag(m.actual, m.target);
              return (
                <div key={m.label} className="text-center">
                  <div className="text-[9px] uppercase tracking-[0.16em] text-neutral-500 mb-1">{m.label}</div>
                  {m.actual === null ? (
                    <div className="text-lg font-bold tabular-nums text-neutral-400">—</div>
                  ) : (
                    <div className="text-lg font-bold tabular-nums" style={{ color: rag.fg }}>
                      {m.actual}<span className="text-[10px] font-normal text-neutral-500">%</span>
                    </div>
                  )}
                  <div className="text-[9px] text-neutral-500 mt-0.5">target {m.target}%</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
